import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorApi, mensajeDe, type TorneoPublico as Datos } from '../services/api';
import { useClienteTorneo } from '../services/clienteTorneo';
import { alTocar } from '../components/ruta';
import { Aviso, Cargando, Tarjeta, Titulo, useTema, Vacio } from '../components/ui';
import { rangoDeFechas } from '../nucleo/fechas';
import { ORIGEN_PUBLICO } from '../nucleo/sitio';
import { temaDeCategoria } from '../nucleo/temas';
import { NOMBRE_FORMATO } from '../nucleo/validar';
import { conTabla } from '../nucleo/vistaGrupos';
import { VistaGrupo } from '../components/Grupos';
import { VistaCuadro } from '../components/Cuadro';
import { resolverCuadroGuardado } from '../nucleo/vistaCuadro';
import { AgendaBuscable, EnCanchaAhora } from '../components/Agenda';
import { VistaPremios } from '../components/Premios';
import { armarAgenda } from '../services/agenda';
import { textoDeHorario } from '../nucleo/programacion';
import { InscripcionPublica } from '../components/InscripcionPublica';
import { ImagenesPublicas } from '../components/ImagenesTorneo';

// La página que circula por WhatsApp: inscriptos, grupos, cuadro, horarios y
// premios (la cara se decidió sobre maqueta/index.html).

type Solapa = 'grupos' | 'cuadro' | 'horarios' | 'premios' | 'inscriptos';
const NOMBRE_SOLAPA: Record<Solapa, string> = {
  grupos: 'Grupos',
  cuadro: 'Cuadro',
  horarios: 'Horarios',
  premios: 'Premios',
  inscriptos: 'Inscriptos',
};

// Cada cuánto se actualiza sola lo dice el servidor (`actualizar_cada_segundos`:
// 5 minutos, o 15 en el torneo de prueba; ver src/nucleo/habilitacion.ts). El que
// quiere el dato ya, toca "Actualizar". Y al volver a la pestaña se pide de nuevo.

/** Para que tocar el botón muchas veces (o entrar y salir de la pestaña) no dispare un pedido por toque. */
const SEGUNDOS_MINIMOS_ENTRE_PEDIDOS = 10;

/** La fecha de hoy del celular, como la guarda el torneo (YYYY-MM-DD). */
function hoy(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "Actualizado recién", "Actualizado hace 3 min". */
function haceCuanto(ms: number): string {
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'Actualizado recién';
  if (min < 60) return `Actualizado hace ${min} min`;
  return `Actualizado hace más de ${Math.floor(min / 60)} h`;
}

export function TorneoPublico({ slug, muestra = false }: { slug: string; muestra?: boolean }) {
  const api = useClienteTorneo();
  const [datos, setDatos] = useState<Datos | null>(null);
  const [error, setError] = useState('');
  const [categoriaId, setCategoriaId] = useState<number | null>(null);
  const [solapa, setSolapa] = useState<Solapa | null>(null);
  const [todasEnHorarios, setTodasEnHorarios] = useState(false);
  // Cuándo llegó lo que se ve, y un reloj para decir "hace 3 min" sin pedir nada.
  const [llegoEn, setLlegoEn] = useState(0);
  const [ahora, setAhora] = useState(Date.now());
  const [actualizando, setActualizando] = useState(false);
  const ultimoPedido = useRef(0);

  const traer = useCallback(
    (primeraVez: boolean) => {
      ultimoPedido.current = Date.now();
      return api.torneoPublico(slug).then(
        (r) => {
          setDatos(r);
          setLlegoEn(Date.now());
          setAhora(Date.now());
          setError('');
          setCategoriaId((actual) => actual ?? r.categorias[0]?.id ?? null);
        },
        (e) => {
          // Si falla una actualización de fondo, se deja lo que ya se veía: en la
          // cancha la señal va y viene, y una pantalla de error no ayuda a nadie.
          if (!primeraVez) return;
          setError(e instanceof ErrorApi && e.estado === 404 ? 'Ese torneo no existe o todavía no se publicó.' : mensajeDe(e));
        },
      );
    },
    [slug, api],
  );

  useEffect(() => {
    void traer(true);
  }, [traer]);

  // Se actualiza sola solo cuando tiene sentido: torneo publicado, en sus
  // fechas, y con la pestaña a la vista. Antes y después del torneo no cambia
  // nada minuto a minuto, y cada pedido cuenta contra el plan de Cloudflare.
  const publicado = datos?.torneo.estado === 'publicado';
  const enFechas = datos ? hoy() >= datos.torneo.fecha_desde && hoy() <= datos.torneo.fecha_hasta : false;
  const enCurso = publicado && enFechas;
  const cadaSegundos = datos?.torneo.actualizar_cada_segundos ?? 300;

  const pedirSiHaceFalta = useCallback(
    (minimoSegundos: number) => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - ultimoPedido.current < minimoSegundos * 1000) return;
      void traer(false);
    },
    [traer],
  );

  useEffect(() => {
    if (!enCurso) return;
    const reloj = setInterval(() => pedirSiHaceFalta(cadaSegundos - 5), cadaSegundos * 1000);
    // Al volver a la pestaña: es lo que hace la gente (abre, mira, guarda el celular).
    const alVolver = () => pedirSiHaceFalta(SEGUNDOS_MINIMOS_ENTRE_PEDIDOS);
    document.addEventListener('visibilitychange', alVolver);
    return () => {
      clearInterval(reloj);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [enCurso, cadaSegundos, pedirSiHaceFalta]);

  // El "hace 3 min" avanza solo, sin pedirle nada al servidor.
  useEffect(() => {
    const reloj = setInterval(() => setAhora(Date.now()), 30_000);
    return () => clearInterval(reloj);
  }, []);

  const actualizar = async () => {
    if (Date.now() - ultimoPedido.current < SEGUNDOS_MINIMOS_ENTRE_PEDIDOS * 1000) return setAhora(Date.now());
    setActualizando(true);
    await traer(false);
    setActualizando(false);
  };

  const categoria = datos?.categorias.find((c) => c.id === categoriaId) ?? null;

  const vista = useMemo(() => {
    if (!categoria) return null;
    const parejas = new Map(categoria.parejas.map((p) => [p.id, p]));
    const bajas = new Set(categoria.parejas.filter((p) => p.estado === 'baja').map((p) => p.id));
    const pasan = categoria.pasan_por_grupo ?? 1;
    const grupos = categoria.grupos.map((g) => conTabla(g, bajas, pasan));
    return {
      parejas,
      grupos,
      letras: new Map(categoria.grupos.map((g) => [g.id, g.letra])),
      cuadro: categoria.cuadro && resolverCuadroGuardado(categoria.cuadro, categoria.grupos, bajas, pasan),
      inscriptas: categoria.parejas.filter((p) => p.estado === 'inscripta'),
    };
  }, [categoria]);

  // Todos los partidos del torneo (todas las categorías), para los horarios y "en cancha ahora".
  const agenda = useMemo(() => (datos ? armarAgenda(datos.categorias) : []), [datos]);
  const hayHorarios = datos?.capacidades.horarios && agenda.some((i) => i.dia && i.hora);
  const cuando = useMemo(
    () => new Map(agenda.filter((i) => !i.jugado).map((i) => [i.id, { texto: textoDeHorario(i), enJuego: i.enJuego }])),
    [agenda],
  );

  // Las solapas que tiene esta categoría, y en cuál se abre: lo que la gente
  // viene a mirar. Con los grupos terminados (o sin grupos), el cuadro.
  const solapas: Solapa[] = [
    ...(vista?.grupos.length ? (['grupos'] as const) : []),
    ...(vista?.cuadro ? (['cuadro'] as const) : []),
    ...(hayHorarios ? (['horarios'] as const) : []),
    // Premios: si el organizador cargó alguno, o si ya hay cuadro (el podio sale de ahí).
    ...(categoria && (categoria.premios.length > 0 || categoria.cuadro) ? (['premios'] as const) : []),
    'inscriptos',
  ];
  const gruposTerminados = vista?.grupos.every((g) => g.tabla.completo) ?? true;
  const porDefecto: Solapa = vista?.cuadro && gruposTerminados ? 'cuadro' : solapas[0]!;
  const solapaVista: Solapa = solapa && solapas.includes(solapa) ? solapa : porDefecto;
  // El tema de la categoría pisa al del torneo (Damas en rosa en un torneo FIP).
  useTema(datos ? temaDeCategoria(datos.torneo.tema, categoria?.tema ?? null) : 'fip');

  if (error) {
    return (
      <div className="mx-auto flex max-w-lg flex-col gap-4 px-4 py-6">
        <a href="/" target={muestra ? '_top' : undefined} onClick={muestra ? undefined : alTocar('/')} className="text-sm font-bold text-(--color-acento)">‹ EnCancha</a>
        <Aviso tono="error">{error}</Aviso>
      </div>
    );
  }
  if (!datos) return <Cargando />;

  const { torneo, categorias } = datos;
  const totalParejas = categorias.reduce((s, c) => s + c.parejas.length, 0);
  const hayGrupos = categorias.some((c) => c.grupos.length > 0);
  const hayCuadro = categorias.some((c) => c.cuadro !== null);

  return (
    <div className="mx-auto flex min-h-screen max-w-lg flex-col">
      <header className="px-4 pt-4 pb-3">
        <a href="/" target={muestra ? '_top' : undefined} onClick={muestra ? undefined : alTocar('/')} className="text-sm font-bold text-(--color-acento)">‹ EnCancha</a>
        {torneo.estado === 'borrador' && (
          <div className="my-2">
            <Aviso tono="alerta">Borrador: esto lo ves porque sos organizador. El público todavía no lo ve.</Aviso>
          </div>
        )}
        <p className="text-xs font-bold uppercase tracking-widest text-(--color-acento)">
          Torneo de pádel{torneo.estado === 'terminado' ? ' · terminado' : ''}
        </p>
        <ImagenesPublicas slug={torneo.slug} imagenes={datos.imagenes ?? []} logo />
        <h1 className="mt-1 font-titulo text-4xl uppercase">{torneo.nombre}</h1>
        <p className="mt-1 text-sm text-(--color-apagado)">
          {rangoDeFechas(torneo.fecha_desde, torneo.fecha_hasta)}
          {torneo.sede ? ` · ${torneo.sede}` : ''}
        </p>
      </header>

      {categorias.length > 0 && (
        <nav className="sticky top-14 z-10 flex gap-2 overflow-x-auto border-b border-(--color-borde) bg-(--color-panel) px-4 py-2.5">
          {categorias.map((c) => (
            <button
              key={c.id}
              onClick={() => {
                setCategoriaId(c.id);
                setSolapa(null);
              }}
              aria-pressed={c.id === categoriaId}
              className={`shrink-0 rounded-full border px-3 py-1 text-sm font-semibold ${
                c.id === categoriaId
                  ? 'border-(--color-acento) bg-(--color-acento) text-(--color-sobre-acento)'
                  : 'border-(--color-borde) text-(--color-apagado)'
              }`}
            >
              {c.nombre}
            </button>
          ))}
        </nav>
      )}

      <main className="flex flex-col gap-4 px-4 py-4">
        <ImagenesPublicas slug={torneo.slug} imagenes={datos.imagenes ?? []} />
        <InscripcionPublica key={torneo.slug} datos={datos} />
        {categorias.length === 0 && <Vacio>Todavía no hay categorías cargadas.</Vacio>}

        {/* Lo que se está jugando ahora va arriba de todo, en cualquier solapa y de cualquier categoría. */}
        <EnCanchaAhora items={agenda} />

        {categoria && vista && solapas.length > 1 && (
          <div className="grid gap-1 border-b border-(--color-borde)" style={{ gridTemplateColumns: `repeat(${solapas.length}, minmax(0, 1fr))` }} role="tablist" aria-label="Secciones de la categoría">
            {solapas.map((s) => (
              <button
                key={s}
                role="tab"
                aria-selected={solapaVista === s}
                onClick={() => setSolapa(s)}
                className={`min-h-11 min-w-0 whitespace-nowrap border-b-3 pb-2 text-xs font-semibold sm:text-sm ${
                  solapaVista === s ? 'border-(--color-acento) text-(--color-texto)' : 'border-transparent text-(--color-apagado)'
                }`}
              >
                {s === 'premios' && !datos.capacidades.premios ? 'Podio' : NOMBRE_SOLAPA[s]}
              </button>
            ))}
          </div>
        )}

        {categoria && vista && solapaVista === 'grupos' &&
          vista.grupos.map((g) => (
            <VistaGrupo key={`${categoria.id}-${g.id}`} grupo={g} parejas={vista.parejas} pasan={categoria.pasan_por_grupo ?? 1} conHorarios={datos.capacidades.horarios} plegable />
          ))}

        {vista?.cuadro && solapaVista === 'cuadro' && <VistaCuadro cuadro={vista.cuadro} parejas={vista.parejas} letras={vista.letras} cuando={cuando} />}

        {categoria && solapaVista === 'horarios' && (
          <>
            <div className="flex gap-2">
              {[false, true].map((todas) => (
                <button
                  key={String(todas)}
                  onClick={() => setTodasEnHorarios(todas)}
                  aria-pressed={todasEnHorarios === todas}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                    todasEnHorarios === todas
                      ? 'border-(--color-acento) bg-(--color-acento) text-(--color-sobre-acento)'
                      : 'border-(--color-borde) text-(--color-apagado)'
                  }`}
                >
                  {todas ? 'Todas las categorías' : `Solo ${categoria.nombre}`}
                </button>
              ))}
            </div>
            <AgendaBuscable items={todasEnHorarios ? agenda : agenda.filter((i) => i.categoriaId === categoria.id)} conCategoria={todasEnHorarios} />
          </>
        )}

        {categoria && vista && solapaVista === 'premios' && (
          <VistaPremios
            linkCompartir={muestra ? `${ORIGEN_PUBLICO}/demo` : undefined}
            recordarPreferencias={!muestra}
            premiosActivos={datos.capacidades.premios}
            categoria={categoria.nombre}
            premios={categoria.premios}
            cuadro={vista.cuadro}
            parejas={vista.parejas}
            torneo={torneo}
            tema={temaDeCategoria(torneo.tema, categoria.tema)}
          />
        )}

        {categoria && vista && solapaVista === 'inscriptos' && (
          <Tarjeta>
            <Titulo extra={`${vista.inscriptas.length} parejas · ${NOMBRE_FORMATO[categoria.formato]}`}>Inscriptos</Titulo>
            {categoria.formato === 'grupos' && categoria.pasan_por_grupo && (
              <p className="-mt-2 mb-3 text-xs text-(--color-apagado)">
                Pasan {categoria.pasan_por_grupo} por grupo al cuadro.
              </p>
            )}
            {vista.inscriptas.length === 0 ? (
              <Vacio>Todavía no hay parejas inscriptas.</Vacio>
            ) : (
              <ol className="flex flex-col">
                {vista.inscriptas.map((p, i) => (
                  <li key={p.id} className="flex items-center gap-3 border-t border-(--color-borde) py-2.5 first:border-t-0">
                    <span className="w-6 text-right text-sm text-(--color-apagado) tabular-nums">{i + 1}</span>
                    <span className="flex-1 font-semibold">
                      {p.jugador1} / {p.jugador2}
                    </span>
                    {p.cabeza_serie !== null && (
                      <span
                        className="rounded-full border border-(--color-acento) px-2 text-xs font-bold text-(--color-acento)"
                        title="Cabeza de serie"
                      >
                        CS {p.cabeza_serie}
                      </span>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </Tarjeta>
        )}

        {totalParejas > 0 && !(hayCuadro && hayHorarios) && (
          <p className="text-center text-xs text-(--color-apagado)">
            {[!hayGrupos && !hayCuadro && 'los grupos', !hayCuadro && 'el cuadro', datos.capacidades.horarios && !hayHorarios && 'los horarios'].filter(Boolean).join(', ').replace(/^./, (l) => l.toUpperCase())}{' '}
            aparecen acá cuando los carguen los organizadores.
          </p>
        )}
        {publicado && (
          <div className="flex flex-col items-center gap-1.5 pt-1">
            <button
              onClick={actualizar}
              disabled={actualizando}
              className="min-h-10 rounded-full border border-(--color-borde) bg-(--color-panel) px-4 text-sm font-semibold disabled:opacity-50"
            >
              {actualizando ? 'Actualizando...' : '↻ Actualizar'}
            </button>
            <p className="text-center text-[11px] text-(--color-apagado)">
              {haceCuanto(ahora - llegoEn)}
              {enCurso ? ` · se actualiza sola cada ${Math.round(cadaSegundos / 60)} min` : ''}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
